"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createI18nApiFactory = void 0;
/* eslint-disable @typescript-eslint/no-empty-function */
const LocalizedString_1 = require("./LocalizedString");
const useGuaranteedMemo_1 = require("powerhooks/useGuaranteedMemo");
const symToStr_1 = require("tsafe/symToStr");
const assert_1 = require("tsafe/assert");
const objectKeys_1 = require("tsafe/objectKeys");
const StatefulObservable_1 = require("powerhooks/tools/StatefulObservable");
const exclude_1 = require("tsafe/exclude");
function createI18nApiFactory(params) {
    const { createUseLang } = params;
    /** @see <https://docs.i18nifty.dev> */
    function createI18nApi() {
        return function (params, translations) {
            const { languages, fallbackLanguage } = params;
            const { useLang, $lang, withLang } = (() => {
                const result = createUseLang({
                    languages,
                    fallbackLanguage
                });
                const { useLang, $lang } = result;
                const { withLang } = result;
                return { useLang, $lang, withLang };
            })();
            const fetchingTranslations = {};
            const fetchedTranslations = Object.fromEntries(Object.entries(translations).filter(([, value]) => typeof value !== "function"));
            const $isFetchingOrNeverFetched = (0, StatefulObservable_1.createStatefulObservable)(() => true);
            function useIsI18nFetching() {
                (0, StatefulObservable_1.useRerenderOnChange)($isFetchingOrNeverFetched);
                return $isFetchingOrNeverFetched.current;
            }
            const $translationFetched = (0, StatefulObservable_1.createStatefulObservable)(() => 0);
            lazy_fetch: {
                if (withLang !== undefined) {
                    //TODO: Support lazy fetching for SSR
                    break lazy_fetch;
                }
                const next = (lang) => {
                    if (fetchedTranslations[lang] !== undefined ||
                        fetchingTranslations[lang] !== undefined) {
                        return;
                    }
                    const fetchTranslations = translations[lang];
                    (0, assert_1.assert)(typeof fetchTranslations === "function");
                    $isFetchingOrNeverFetched.current = true;
                    const pr = fetchTranslations();
                    fetchingTranslations[lang] = pr;
                    pr.then(translation => {
                        fetchingTranslations[lang] = undefined;
                        fetchedTranslations[lang] = translation;
                        $translationFetched.current++;
                        const notifyIfNoLongerFetchingIfItsTheCase = () => {
                            if ((0, objectKeys_1.objectKeys)(fetchingTranslations)
                                .map(lang => fetchingTranslations[lang])
                                .filter((0, exclude_1.exclude)(undefined)).length !== 0) {
                                return;
                            }
                            $isFetchingOrNeverFetched.current = false;
                        };
                        if (lang === fallbackLanguage ||
                            fetchedTranslations[fallbackLanguage] !== undefined) {
                            notifyIfNoLongerFetchingIfItsTheCase();
                            return;
                        }
                        if ((0, objectKeys_1.objectKeys)(translation)
                            .map(componentName => translation[componentName])
                            .map(componentTranslation => (0, objectKeys_1.objectKeys)(componentTranslation)
                            .map(key => componentTranslation[key])
                            .includes(undefined))
                            .flat()
                            .includes(true)) {
                            next(fallbackLanguage);
                        }
                        else {
                            notifyIfNoLongerFetchingIfItsTheCase();
                        }
                    });
                };
                next($lang.current);
                $lang.subscribe(next);
            }
            function useResolveLocalizedString(params) {
                const { labelWhenMismatchingLanguage } = params !== null && params !== void 0 ? params : {};
                const { lang } = useLang();
                const { resolveLocalizedString, resolveLocalizedStringDetailed } = (0, useGuaranteedMemo_1.useGuaranteedMemo)(() => {
                    const { resolveLocalizedString, resolveLocalizedStringDetailed } = (0, LocalizedString_1.createResolveLocalizedString)({
                        "currentLanguage": lang,
                        fallbackLanguage,
                        "labelWhenMismatchingLanguage": labelWhenMismatchingLanguage
                    });
                    return {
                        resolveLocalizedString,
                        resolveLocalizedStringDetailed
                    };
                }, [
                    lang,
                    typeof labelWhenMismatchingLanguage === "object"
                        ? labelWhenMismatchingLanguage.ifStringAssumeLanguage
                        : labelWhenMismatchingLanguage
                ]);
                return {
                    resolveLocalizedString,
                    resolveLocalizedStringDetailed
                };
            }
            function getTranslationForLanguage(params) {
                const { componentName, getLang } = params;
                const t = (key, params) => {
                    const lang = getLang();
                    if (fetchedTranslations[lang] === undefined) {
                        return "";
                    }
                    const getStrOrFn = (lang) => {
                        //return (fetchedTranslations as any)[lang][componentName][key];
                        const translation = fetchedTranslations[lang];
                        if (translation === undefined) {
                            return "";
                        }
                        return translation[componentName][key];
                    };
                    let strOrFn = getStrOrFn(lang);
                    if (strOrFn === undefined) {
                        strOrFn = getStrOrFn(fallbackLanguage);
                    }
                    return params === undefined ? strOrFn : strOrFn(params);
                };
                return { t };
            }
            function useTranslation(componentNameAsKey) {
                const { lang } = useLang();
                (0, StatefulObservable_1.useRerenderOnChange)($translationFetched);
                const componentName = (0, symToStr_1.symToStr)(componentNameAsKey);
                const { t } = (0, useGuaranteedMemo_1.useGuaranteedMemo)(() => getTranslationForLanguage({
                    "getLang": () => lang,
                    componentName
                }), [lang, componentName, $translationFetched.current]);
                return { t };
            }
            function getTranslation(componentName) {
                const { t } = getTranslationForLanguage({
                    componentName,
                    "getLang": () => $lang.current
                });
                return { t };
            }
            function resolveLocalizedString(localizedString, options) {
                const { labelWhenMismatchingLanguage } = options !== null && options !== void 0 ? options : {};
                const { resolveLocalizedString } = (0, LocalizedString_1.createResolveLocalizedString)({
                    "currentLanguage": $lang.current,
                    fallbackLanguage,
                    "labelWhenMismatchingLanguage": labelWhenMismatchingLanguage
                });
                return resolveLocalizedString(localizedString);
            }
            const i18nApi = {
                useLang,
                useTranslation,
                useResolveLocalizedString,
                resolveLocalizedString,
                $lang,
                useIsI18nFetching,
                getTranslation
            };
            return Object.assign(Object.assign({}, i18nApi), { withLang });
        };
    }
    return { createI18nApi };
}
exports.createI18nApiFactory = createI18nApiFactory;
